import { AuthProvider } from "@services/env";

// ============================================================================
// Roles
// ============================================================================

export type TRoleCategory = "dev_team" | "administrator" | "project_owner" | "pi_viewer" | "non_pi_viewer" | "cm_user" | "cm_operator" | "none";

export interface TRole {
  userGroup: string;
  role: TRoleCategory;
}

/** Human-readable labels for each role, used for display (e.g. the user info panel). */
export const RoleLabels: Record<TRoleCategory, string> = {
  dev_team: "Dev Team",
  administrator: "Administrator",
  project_owner: "Project Owner",
  pi_viewer: "PI Viewer",
  non_pi_viewer: "Non-PI Viewer",
  cm_user: "CM Engineer",
  cm_operator: "Operator",
  none: "—",
};

/**
 * Resolve a display label for the user's role, tolerating both the flat `role` string
 * and the legacy `roles` array. Returns undefined when no meaningful role is present.
 */
export function getRoleLabel(user: Pick<IUser, "role" | "roles">): string | undefined {
  const role = user.role ?? user.roles?.find(r => r.role !== "none")?.role ?? user.roles?.[0]?.role;
  if (!role || role === "none") return undefined;
  return RoleLabels[role];
}

// ============================================================================
// Authenticated User (current session)
// ============================================================================

export interface IUser {
  username: string;
  email?: string;
  sub?: string;
  givenName?: string;
  familyName?: string;
  authProvider?: AuthProvider;
  /**
   * The user's role as returned by the backend `get_user_info` endpoint (a single
   * flat string, e.g. "cm_user"). Prefer reading this via {@link hasRole}.
   */
  role?: TRoleCategory;
  /**
   * Legacy/aspirational multi-role array. The current backend does not populate this;
   * it returns a single `role` string. Kept optional for backward compatibility.
   */
  roles?: TRole[];
  /** Whether the user account is activated. Only relevant for Cognito (operator) users. */
  activated?: boolean;
}

/**
 * Returns true if the user has any of the given roles.
 *
 * Handles both the current backend contract (a flat `role` string) and the legacy
 * `roles` array, so callers don't have to care which shape the backend returned.
 */
export function hasRole(user: Pick<IUser, "role" | "roles">, ...roles: TRoleCategory[]): boolean {
  if (user.role && roles.includes(user.role)) return true;
  return (user.roles ?? []).some(r => roles.includes(r.role));
}

// ============================================================================
// Operator Management (admin page — CM Engineer manages Cognito operators)
// ============================================================================

/** Operator account status shown in the admin table. */
export type OperatorStatus = "active" | "inactive" | "pending" | "disabled" | "expired";

/** A device record from Cognito device tracking, embedded in each operator. */
export interface IOperatorDevice {
  deviceKey: string; // Cognito device key
  deviceName: string; // e.g., "Chrome 120 / macOS 14.2"
  lastIpUsed?: string;
  lastAuthenticatedDate: string; // ISO datetime
  createDate: string; // ISO datetime
}

/** A Cognito operator row in the admin operator table. */
export interface IOperator {
  username: string;
  status: OperatorStatus;
  activated: boolean;
  createDate: string; // ISO datetime
  expiryDate?: string; // ISO datetime — set on activate (1-week expiry); absent until activated
  devices: IOperatorDevice[]; // all tracked devices, newest first
}

/** Request body for creating a new operator. */
export interface ICreateOperatorRequest {
  username: string;
  firstName: string;
  lastName: string;
  site: string; // test location / site the operator belongs to (required by BE)
}
